import { captureRequestScope } from '@/lib/api/request-scope'
import { create } from 'zustand'

import type { SessionAnchor } from '@scrambl/shared'

export interface BennyToolCallState {
  toolCallId: string
  toolName: string
  status: 'working' | 'completed' | 'failed'
  durationMs?: number
  error?: string
  /** The call's arguments, when the stream carried them — for the pill label. */
  args?: unknown
  /** Position within the reply's batch, when it holds more than one call. */
  step?: number
  stepCount?: number
}

export interface BennyStreamingState {
  /** Server-assigned id of the assistant message currently streaming. */
  messageId: string
  /** Accumulated content so far. */
  content: string
  /** Tool calls observed during this stream, in order they started. */
  toolCalls: BennyToolCallState[]
}

interface BennyStore {
  open: boolean
  maximized: boolean
  mode: 'docked' | 'popover'
  setMode: (mode: 'docked' | 'popover') => void
  setOpen: (open: boolean) => void
  toggle: () => void
  setMaximized: (maximized: boolean) => void

  /**
   * One-shot prompt prefill: set together with open=true (e.g. from the
   * Guided Setup Benny dock) and consumed by BennyChat, which copies it into
   * the input box and clears it. Never auto-sends — the user presses Enter.
   */
  pendingAnchor: SessionAnchor | null
  pendingOwner: AbortSignal | null
  pendingSessionId: string | undefined
  openAbout: (
    anchor: SessionAnchor,
    prompt?: string,
    sessionId?: string,
  ) => void
  consumePendingAnchor: () => SessionAnchor | null
  pendingPrompt: string | null
  openWithPrompt: (prompt: string) => void
  consumePendingPrompt: () => string | null
  /**
   * One-shot hand-back from the full Conversations page ("Pin to side"):
   * open the chat on that conversation (`sessionId` undefined = the blank
   * new conversation the page was showing) instead of the latest session.
   */
  pendingResume: { sessionId: string | undefined } | null
  resumeSession: (sessionId: string | undefined) => void
  consumePendingResume: () => { sessionId: string | undefined } | null

  /**
   * Whether the floating desktop companion window is currently alive.
   * Set by BennyCompanionController from companion ready/disabled events;
   * used to decide whether notifications should be delivered through Benny
   * instead of native system notifications.
   */
  companionActive: boolean
  setCompanionActive: (companionActive: boolean) => void

  /**
   * Streaming state for the in-flight assistant turn. `null` means no stream
   * is currently active. The frontend renders the streaming bubble directly
   * from this state until the stream ends, at which point React Query takes
   * over with the persisted version.
   */
  streamingState: BennyStreamingState | null
  abortController: AbortController | null
  startStream: (messageId: string, abortController: AbortController) => void
  appendDelta: (messageId: string, delta: string) => void
  startToolCall: (
    toolCallId: string,
    toolName: string,
    args?: unknown,
    progress?: { step?: number; stepCount?: number },
  ) => void
  endToolCall: (
    toolCallId: string,
    status: 'completed' | 'failed',
    durationMs: number,
    error?: string,
  ) => void
  endStream: (messageId?: string) => void
  cancelStream: () => void
}

export const useBennyStore = create<BennyStore>((set, get) => ({
  open: false,
  maximized: false,
  mode: 'docked',
  setMode: mode => set({ mode, maximized: false }),
  setOpen: open => set({ open, ...(!open ? { maximized: false } : {}) }),
  toggle: () =>
    set(s => ({ open: !s.open, ...(!s.open ? {} : { maximized: false }) })),
  setMaximized: maximized => set({ maximized }),

  pendingAnchor: null,
  pendingOwner: null,
  pendingSessionId: undefined,
  openAbout: (anchor, prompt, sessionId) =>
    set({
      open: true,
      pendingAnchor: anchor,
      pendingOwner: captureRequestScope().signal,
      pendingSessionId: sessionId,
      pendingPrompt: prompt ?? null,
    }),
  consumePendingAnchor: () => {
    const anchor = get().pendingOwner?.aborted ? null : get().pendingAnchor
    set({ pendingAnchor: null, pendingSessionId: undefined })
    return anchor
  },
  pendingPrompt: null,
  openWithPrompt: prompt =>
    set({
      open: true,
      pendingPrompt: prompt,
      pendingAnchor: null,
      pendingSessionId: undefined,
      pendingOwner: captureRequestScope().signal,
    }),
  consumePendingPrompt: () => {
    const prompt = get().pendingOwner?.aborted ? null : get().pendingPrompt
    set({ pendingPrompt: null })
    return prompt
  },
  pendingResume: null,
  resumeSession: sessionId => set({ open: true, pendingResume: { sessionId } }),
  consumePendingResume: () => {
    const resume = get().pendingResume
    set({ pendingResume: null })
    return resume
  },

  companionActive: false,
  setCompanionActive: companionActive => set({ companionActive }),

  streamingState: null,
  abortController: null,
  startStream: (messageId, abortController) =>
    set({
      streamingState: { messageId, content: '', toolCalls: [] },
      abortController,
    }),
  appendDelta: (messageId, delta) =>
    set(s => {
      // Only append to the active streaming message; later events for older
      // messages are ignored (defensive against late frames after abort).
      if (!s.streamingState || s.streamingState.messageId !== messageId)
        return s
      return {
        streamingState: {
          ...s.streamingState,
          content: s.streamingState.content + delta,
        },
      }
    }),
  startToolCall: (toolCallId, toolName, args, progress) =>
    set(s => {
      if (!s.streamingState) return s
      // De-dupe in case the backend re-sends the same tool_call_start.
      if (s.streamingState.toolCalls.some(tc => tc.toolCallId === toolCallId))
        return s
      return {
        streamingState: {
          ...s.streamingState,
          toolCalls: [
            ...s.streamingState.toolCalls,
            { toolCallId, toolName, status: 'working', args, ...progress },
          ],
        },
      }
    }),
  endToolCall: (toolCallId, status, durationMs, error) =>
    set(s => {
      if (!s.streamingState) return s
      return {
        streamingState: {
          ...s.streamingState,
          toolCalls: s.streamingState.toolCalls.map(tc =>
            tc.toolCallId === toolCallId
              ? { ...tc, status, durationMs, error }
              : tc,
          ),
        },
      }
    }),
  endStream: messageId =>
    set(s => {
      // Ownership check: a turn's `finally` can resolve after the user has
      // already started a NEW stream (isLoading drops before the cache
      // refetch awaits). Only clear if the active stream is ours — when a
      // messageId is provided and a different message owns the stream,
      // leave it untouched.
      if (
        messageId &&
        s.streamingState &&
        s.streamingState.messageId !== messageId
      ) {
        return s
      }
      return { streamingState: null, abortController: null }
    }),
  cancelStream: () => {
    // Just abort. The handleSend caller's `finally` clause is the single
    // source of truth for clearing streamingState — clearing it here too
    // races with React Query's refetch and can flicker the message back
    // and forth.
    const ctrl = get().abortController
    if (ctrl) ctrl.abort()
  },
}))
